import classNames from 'classnames'
import { useTranslation } from 'next-i18next'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts/NoProduct'
import { Filter } from '@src/quince-components/src/atoms/filterSection/menu/filterSection.interface'
import { SkeletonPlaceholders } from '@src/quince-components/src/atoms/skeletonPlaceholders/SkeletonPlaceholders'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

import { getTrackingMetaData, PRODUCT_CARD_OPTION_LIMIT, PRODUCT_VIEW_TRACKER } from './searchResults.constants'
import styles from './searchResults.module.scss'

interface SearchResultsContentProps {
  productCards: IProductCard[]
  showLoader: boolean
  maxWidth: number
  productViewTracker?: string
  searchQuery: string
  onResetFilters: () => void
  filters: Filter[]
  isNewCLPFiltersExperimentActive: boolean
  showDesktopFilterWithSidebar: boolean
}

export const SearchResultsContent = ({
  productCards,
  showLoader,
  maxWidth,
  searchQuery,
  onResetFilters,
  filters,
  isNewCLPFiltersExperimentActive,
  showDesktopFilterWithSidebar,
}: SearchResultsContentProps) => {
  const { t } = useTranslation()

  if (showLoader) {
    return (
      <div className={styles.searchResultsWrapper}>
        <SkeletonPlaceholders length={10} />
      </div>
    )
  }

  if (productCards.length > 0) {
    return (
      <div
        className={classNames(styles.searchResultsWrapper, {
          [styles.searchResultsWrapper__newCLPFilters ?? '']: isNewCLPFiltersExperimentActive,
          [styles.searchResultsWrapper__newCLPFiltersDesktopGroupB ?? '']: showDesktopFilterWithSidebar,
        })}
      >
        {productCards.map((productCard, pIdx) => {
          const { product } = productCard
          const initialSelectedVariant = getInitialSelectedVariant({
            cardVariants: product.cardVariants,
          })
          return (
            <ProductCard
              shouldShowMore
              optionLimit={PRODUCT_CARD_OPTION_LIMIT}
              maxWidth={maxWidth}
              key={`${product.productId}_${initialSelectedVariant?.displayConfig.value}_${pIdx}`}
              className={styles.productCard}
              productCard={productCard}
              tracker={PRODUCT_VIEW_TRACKER}
              productViewTracker={PRODUCT_VIEW_TRACKER}
              searchQuery={searchQuery}
              position={pIdx + 1}
              productClickTrackConfig={{
                trackInternal: true,
                category: 'search_results_collection',
                action: 'search_by_query',
                context: {
                  searchQuery: searchQuery,
                  position: pIdx,
                  productPosition: pIdx,
                  ...getTrackingMetaData().context,
                },
              }}
              onClickCb={() => {
                setFindingMethod[FINDING_METHOD_AGGREGATE.SEARCH]?.()
              }}
            />
          )
        })}
      </div>
    )
  }

  return (
    <>
      {isNewCLPFiltersExperimentActive && !filters.length && (
        <h1
          className={`${styles.searchResultsHeader__title} ${styles.searchResultsHeader__title__noResults}`}
        >{`"${searchQuery.toUpperCase()}"`}</h1>
      )}
      <div className={styles.noSearchResultsWrapper} data-testid={TEST_LOCATORS.NO_SEARCH_RESULTS}>
        {filters.length ? (
          <NoProduct
            onResetFilters={onResetFilters}
            isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
          />
        ) : (
          t('sorry_we_couldnt_find_any_matching_results_for_this_search')
        )}
      </div>
    </>
  )
}
